import type { ClientClassificationInput } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as clientClassificationApi from "@/lib/api/flow-instances/client-classification";
import { reuseKeys } from "./reuse-keys";
import { useInvalidateFlowCompletion } from "./use-flow-completion";
import { invalidateSuitabilityQueries } from "./use-goals";
import { useInvalidateDynamicSync } from "./use-portfolios";

export const clientClassificationKeys = {
  flowInstanceValue: (id: string | null) =>
    ["flowInstances", id, "client-classification", "flow-instance-value"] as const,
};

export function useClientClassificationFlowInstanceValue(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: clientClassificationKeys.flowInstanceValue(id),
    queryFn: () => clientClassificationApi.getFlowInstanceValue(id!),
    enabled: !!id,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

export function useSaveClientClassification(instanceId: string) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  const invalidateDynamicSync = useInvalidateDynamicSync();
  return useMutation({
    mutationFn: (input: ClientClassificationInput) =>
      clientClassificationApi.saveFlowInstanceValue(instanceId, input),
    onSuccess: (result) => {
      if (result.success && result.inputData !== undefined) {
        queryClient.setQueryData(clientClassificationKeys.flowInstanceValue(instanceId), {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
        // Slice 5 — saving the classification populates calculatedData, which is the engine's
        // completion signal for client_classification.
        invalidateCompletion(instanceId);
        // Manual entry fills the component → the reuse button must hide. Refetch availability.
        queryClient.invalidateQueries({
          queryKey: reuseKeys.availability(instanceId, "client_classification"),
        });
        // QL-584 — the save regenerated dynamic portfolios.
        invalidateDynamicSync(result.dynamicSync);
      }
    },
    onSettled: () => {
      invalidateSuitabilityQueries(queryClient);
    },
  });
}
